"use client";
import React, { useState, useEffect } from "react";
import { useAppContext } from "@components/Provider";
import Button from "../Button/Button";
import { Post } from "src/services/ClientSideAPIService";
import Validator from "../../helpers/Validator";
import { readCookie } from "../../helpers/Helper";
import Browser from "../../utils/browser";
import Modal from "../Modal/Modal";
import Link from "next/link";
import BookImage from "./GiveawayBookImage";
import Meta from "../Meta";
import Entity from "../../components/Entity";
import Error404 from "@components/Error404/Error404";

import styles from "./GiveAwayPage.module.css";

const GiveAwayPage = (props) => {
  const [postData, giveawayPageData] = props.data;
  const { post } = postData.Post;
  const { image, primary_isbn, amazon_isbn13, title } = giveawayPageData?.GiveawayPage || {};
  const { siteId, data } = useAppContext();
  const { SiteSettings } = data || {};

  const url = new URL(props?.resultProps?.PageUrl);
  let q = url && url.searchParams.get("q");

  useEffect(() => {
    renderGiveawayCopy();
  }, []);

  const [state, setState] = useState({
    email: "",
    submitInProgress: false,
    validationErrors: [],
    content: {},
    isModalOpen: false,
    download: false,
    downloadLinks: "",
  });

  const handleEmailChange = (e) => {
    const email = e.currentTarget.value;
    setState((prevState) => ({ ...prevState, email }));
  };

  const enterPressEvent = (e) => {
    if (e.key == "Enter") handleSubmit(e);
  };

  const renderEmailInput = () => {
    return (
      <div className={styles["email-input"]}>
        <div className={styles["email-input__title"]}>Email Address</div>
        <input
          className={[styles["e-input"], styles["email-input__input"]].join(" ")}
          type="email"
          value={state.email}
          onChange={handleEmailChange}
          placeholder="youremailaddress@address.com"
          autoFocus="autofocus"
          onKeyPress={enterPressEvent}
          required
        />
      </div>
    );
  };

  const getSailthruVars = () => {
    let tracking_cookie = readCookie("utm_params");
    const utmParams = tracking_cookie ? JSON.parse(tracking_cookie) : null;
    const signupLocation = "giveaway_page";

    return {
      site_id: props.siteId,
      vars: {
        signup_location: signupLocation,
        slug: Browser.getHostname(),
        referrer: Browser.getReferrer(),
        onboard_reward: `${primary_isbn}`,
        ...utmParams,
      },
    };
  };

  const validateSubmit = (e) => {
    e.preventDefault();
    setState((prevState) => ({ ...prevState, submitInProgress: true }));
    let errors = [];
    const { email } = state;
    if (!email) errors.push("Please provide an email address");
    if (Validator.valideEmail(email) === false)
      errors.push("Please submit a valid email address");

    if (errors.length) {
      setState((prevState) => ({
        ...prevState,
        submitInProgress: false,
        validationErrors: errors,
      }));
      return false;
    } else {
      const newsletter = {};
      newsletter.userOptionalParams = getSailthruVars();
      newsletter.name = "earlybirdbooks";
      return {
        newsletters: [newsletter],
        email: email,
      };
    }
  };

  const handleSubmit = async (e) => {
    const data = validateSubmit(e);
    if (data) {
      const formData = data;
      await Post(
        "SignUp",
        { data: formData, siteId },
        async () => {
          await Post(
            "GetePubMobiUrls",
            {
              data: {
                primary_isbn,
                amazon_isbn13: amazon_isbn13 || null,
                title,
                type: "Giveaway_Page",
              },
            },
            (success) => {
              if (
                success.hasOwnProperty("mobiUrl") ||
                success.hasOwnProperty("ePubUrl")
              ) {
                setState((prevState) => ({
                  ...prevState,
                  downloadLinks: success,
                  download: true,
                  submitInProgress: false,
                  validationErrors: [],
                  isModalOpen: true,
                }));
              } else {
                setState((prevState) => ({
                  ...prevState,
                  submitInProgress: false,
                  isModalOpen: true,
                  downloadLinks: {},
                }));
                return false;
              }
            },
            (error) => {
              console.log(error);
            }
          );
        },
        (error) => {
          if (error) {
            console.log("signup error", error);
            let message = "An error occurred while submitting this form";
            setState((prevState) => ({
              ...prevState,
              validationErrors: [message],
              submitInProgress: false,
              download: false,
            }));
          }
        }
      );
    } else {
      return false;
    }
  };

  const renderErrors = () => {
    const { validationErrors } = state;
    if (validationErrors && validationErrors.length) {
      return (
        <ul className={[styles["ul"], styles["giveawaypage__errors"]].join(" ")}>
          {validationErrors.map((error, key) => {
            return (
              <li key={`error-${key}`}>
                <div className={styles["error"]}>{error}</div>
              </li>
            );
          })}
        </ul>
      );
    } else {
      return null;
    }
  };

  const renderdownloadLinks = () => {
    const {
      downloadWindowCopy,
      mobiEpubButtonColor,
      epubButtonText,
      mobiButtonText,
    } = state.content;
    const { downloadLinks, download } = state;
    return (
      <React.Fragment>
        {(download && downloadLinks.mobiUrl) || downloadLinks.ePubUrl ? (
          <div className={styles["giveaway__page__button__links"]}>
            <div
              className={styles["giveaway__page__downloadwindow__copy"]}
              dangerouslySetInnerHTML={{ __html: downloadWindowCopy }}
            />
            {downloadLinks.mobiUrl && (
              <Link
                href={downloadLinks.mobiUrl}
                style={{ background: mobiEpubButtonColor }}
              >
                {mobiButtonText}
              </Link>
            )}
            {downloadLinks.ePubUrl && (
              <Link
                href={downloadLinks.ePubUrl}
                style={{ background: mobiEpubButtonColor }}
              >
                {epubButtonText}
              </Link>
            )}
          </div>
        ) : (
          <div>The Selected Book is not Available for Download</div>
        )}
      </React.Fragment>
    );
  };

  const renderGiveawayCopy = () => {
    let newContent = {},
      contentArray = [];
    post.components.forEach((data) => {
      switch (data.type) {
        case "content":
          contentArray.push(data.props.value);
          newContent["giveawayCopy"] = contentArray[0];
          newContent["emailCopy"] = contentArray[1];
          newContent["downloadWindowCopy"] = contentArray[2];
          break;
        case "button-text-link":
          newContent["buttonColor"] = data.props.buttonColor;
          newContent["buttonText"] = data.props.buttonText;
          newContent["backgroundColor"] = data.props.backgroundColor;
          newContent["mobiEpubButtonColor"] = data.props.mobiEpubButtonColor;
          newContent["mobiButtonText"] = data.props.mobiButtonText;
          newContent["epubButtonText"] = data.props.epubButtonText;
      }
    });
    setState((prevState) => ({ ...prevState, content: newContent }));
  };

  const toggleModal = () =>
    setState((prevState) => ({
      ...prevState,
      isModalOpen: !state.isModalOpen,
    }));

  const { giveawayCopy, emailCopy, buttonColor, buttonText, backgroundColor } =
    state.content;
  const { title: Title, description, no_index } = post.seo;
  const { site_name } = SiteSettings;
  const seoTitle = `${Title} - ${title} | ${site_name}`;
  const SEO_Data = {
    Title: seoTitle,
    Description: `${description} ${title}`,
  };
  return q ? (
    <div className={styles["giveaway__page"]} style={{"--bookcopy-bg-color": backgroundColor}}>
      <Meta
        title={seoTitle}
        static={SEO_Data}
        pageType={Entity.STATIC}
        url={props.PageUrl}
        noIndex={true}
        isNewAppRouter={true}
      />

      <section className={[styles["section"], styles["giveaway__page__bookcopy"]].join(" ")}>
        {giveawayCopy && (
          <div
            className={styles["giveaway__copy"]}
            dangerouslySetInnerHTML={{ __html: giveawayCopy }}
          />
        )}
        <div className={styles["giveawaypage__book_image"]}>
          <BookImage ImageUrl={image} Title={title} />
        </div>
      </section>
      <section>
        {emailCopy && (
          <div
            className={styles["giveaway__page__emailCopy"]}
            dangerouslySetInnerHTML={{ __html: emailCopy }}
          />
        )}
        <div>
          {renderEmailInput()}
          {renderErrors()}
          {state.isModalOpen && (
            <Modal
              isOpen={state.isModalOpen}
              closeBtn={toggleModal}
              className={"download-modal"}
              transitionName={"modal-anim"}
            >
              {renderdownloadLinks()}
            </Modal>
          )}
        </div>
        <div className={styles["giveawaypage__download_button_section"]}>
          <Button
            classes="button submit-button"
            type="submit"
            text={
              state.submitInProgress ? "Getting your free ebook..." : buttonText
            }
            handleClick={handleSubmit}
            disabled={state.submitInProgress}
            inlineStyles={{ background: buttonColor || "#2c67b0" }}
          />
        </div>
      </section>
    </div>
  ) : (
    <Error404 />
  );
};

export default GiveAwayPage;
